import { Link, Navigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Factory, Users, ChevronLeft, CalendarClock, UserCog, Receipt, Wallet, Gift, Store, FileText } from "lucide-react";

/**
 * Two-tile chooser: Client Ledger / Factory Ledger.
 * Reused on home page (/) and Records page (/history).
 */
function Tile({ to, icon: Icon, title, subtitle, color, testId }) {
    return (
        <Link
            to={to}
            className={`group relative flex flex-col gap-3 overflow-hidden rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg sm:p-8 ${color}`}
            data-testid={testId}
        >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/90 ring-1 ring-neutral-200 sm:h-16 sm:w-16">
                <Icon className="h-7 w-7 text-amber-700 sm:h-8 sm:w-8" />
            </div>
            <div className="mt-2">
                <h3 className="font-heading text-xl font-bold sm:text-2xl">{title}</h3>
                <p className="mt-1 text-sm text-neutral-600">{subtitle}</p>
            </div>
            <div className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-amber-700">
                <ChevronLeft className="h-4 w-4 transition group-hover:-translate-x-1" />
            </div>
        </Link>
    );
}

export default function LedgerPicker() {
    const { t, lang } = useLang();
    const { company } = useAuth();
    // Car Learn account: the home page IS the customer ledger — no
    // gold-system tiles are shown. Redirect straight there.
    if (company?.car_learn) return <Navigate to="/car-bookings" replace />;
    if (company?.mansori_shop) return <MansoriShopHome lang={lang} />;
    return <LedgerPickerInner t={t} lang={lang} company={company} />;
}

function MansoriShopHome() {
    const sections = [
        ["الدفاتر", "/mshop", CalendarClock, "دفتر الحركة", "بيع ذهب جديد • شراء ذهب مستعمل", "picker-mshop-movement"],
        ["السجلات", "/mshop/records", FileText, "سجلات الحركة", "سجلات البيع والشراء حسب السنة والشهر واليوم", "picker-mshop-records"],
        ["سجل المحل", "/mshop/shop-log", Store, "سجل المحل", "الذهب والكاش الحاليان والمبيعات والمشتريات والفوائد", "picker-mshop-shop-log"],
    ];
    return (
        <AppShell>
            <div className="space-y-8" dir="rtl" data-testid="mansori-shop-home">
                {sections.map(([kicker, to, icon, title, sub, testId]) => (
                    <section key={to}>
                        <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-amber-700">{kicker}</p>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <Tile to={to} icon={icon} title={title} subtitle={sub} color="bg-gradient-to-br from-white to-amber-50/40" testId={testId} />
                        </div>
                    </section>
                ))}
            </div>
        </AppShell>
    );
}

function LedgerPickerInner({ t, lang, company }) {
    const isManager = company?.account_type === "manager";
    const isEmployee = company?.account_type === "employee";
    const enabled = new Set(company?.enabled_ledgers || ["factory", "client", "daily"]);
    const showFactory = !isEmployee && enabled.has("factory");
    const showClient = !isEmployee && enabled.has("client");
    // Managers don't have a *personal* daily ledger — daily is for employees.
    const showDaily = enabled.has("daily") && company?.ledger_mode !== "basic" && !isManager;
    const showVouchers = !isEmployee && enabled.has("vouchers");
    // Shop Log — required for individuals and managers. Employees never
    // see it (per spec: their daily-ledger flows contribute to the manager's
    // aggregate view, but they don't get their own Shop Log tile).
    const showShopLog = !isEmployee && (isManager || enabled.has("daily"));
    return (
        <AppShell>
            <div className="mb-6 sm:mb-8">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.pickerLedgersKicker}</p>
                <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{t.pickerLedgersTitle}</h1>
                <p className="mt-1 text-xs text-neutral-500 sm:text-sm">{t.pickerLedgersSubtitle}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                {showClient ? (
                    <Tile
                        to="/client"
                        icon={Users}
                        title={t.clientLedgerTitle}
                        subtitle={t.clientLedgerSubtitle}
                        color="bg-gradient-to-br from-white to-amber-50/40"
                        testId="picker-client-ledger"
                    />
                ) : null}
                {showFactory ? (
                    <Tile
                        to="/factory"
                        icon={Factory}
                        title={t.factoryLedgerTitle}
                        subtitle={t.factoryLedgerSubtitle}
                        color="bg-gradient-to-br from-white to-emerald-50/30"
                        testId="picker-factory-ledger"
                    />
                ) : null}
                {showDaily ? (
                    <Tile
                        to="/daily"
                        icon={CalendarClock}
                        title={company?.daily_variant === "advanced" ? t.advTitle : t.dailyLedgerTitle}
                        subtitle={company?.daily_variant === "advanced" ? t.dvAdvancedDesc : t.dailyLedgerSubtitle}
                        color={
                            company?.daily_variant === "advanced"
                                ? "bg-gradient-to-br from-white to-indigo-50/40"
                                : "bg-gradient-to-br from-white to-sky-50/40"
                        }
                        testId="picker-daily-ledger"
                    />
                ) : null}
                {showVouchers ? (
                    <>
                        <Tile
                            to="/voucher/customer"
                            icon={Receipt}
                            title={t.voucherCustomerTile}
                            subtitle={t.voucherCustomerTileDesc}
                            color="bg-gradient-to-br from-white to-rose-50/40"
                            testId="picker-voucher-customer"
                        />
                        <Tile
                            to="/voucher/shop"
                            icon={Wallet}
                            title={t.voucherShopTile}
                            subtitle={t.voucherShopTileDesc}
                            color="bg-gradient-to-br from-white to-violet-50/40"
                            testId="picker-voucher-shop"
                        />
                    </>
                ) : null}
                {isEmployee && showDaily ? (
                    <Tile
                        to="/history/daily"
                        icon={FileText}
                        title={lang === "ar" ? "سجلاتي" : "My records"}
                        subtitle={lang === "ar" ? "سجلات الحركة التي حفظتها — السنة ثم الشهر ثم اليوم" : "Your saved daily records — year → month → day"}
                        color="bg-gradient-to-br from-white to-sky-50/40"
                        testId="picker-employee-records"
                    />
                ) : null}
                {isManager && !company?.mansori_manager ? (
                    <Tile
                        to="/employees"
                        icon={UserCog}
                        title={t.employeeRecordsTitle}
                        subtitle={t.employeeRecordsSubtitle}
                        color="bg-gradient-to-br from-white to-amber-50/30"
                        testId="picker-employees"
                    />
                ) : null}
                {showShopLog ? (
                    <Tile
                        to="/shop-log"
                        icon={Store}
                        title={lang === "ar" ? "سجل المحل" : "Shop Log"}
                        subtitle={lang === "ar" ? "لمحة سريعة عن وضع المحل: الذهب، الكاش، المبيعات، المشتريات" : "Quick shop overview: gold, cash, sales, purchases"}
                        color="bg-gradient-to-br from-white to-emerald-50/60"
                        testId="picker-shop-log"
                    />
                ) : null}
            </div>
            {isEmployee && !showDaily ? (
                <div className="mt-4 rounded-2xl border border-dashed border-neutral-300 bg-white/60 p-10 text-center text-sm text-neutral-500" data-testid="employee-no-ledgers">
                    {lang === "ar" ? "لم يفعّل المدير لك أي دفتر بعد. تواصل مع مديرك." : "Your manager hasn't enabled any ledger for you yet."}
                </div>
            ) : null}
        </AppShell>
    );
}

export function RecordsPicker() {
    const { t, lang } = useLang();
    const { company } = useAuth();
    // Car Learn account: records show ONLY car-bookings, grouped by
    // month/year — no gold-system records tiles.
    if (company?.car_learn) return <Navigate to="/car-bookings/records" replace />;
    const isManager = company?.account_type === "manager";
    const isEmployee = company?.account_type === "employee";
    const enabled = new Set(company?.enabled_ledgers || ["factory", "client", "daily"]);
    const showFactory = !isEmployee && enabled.has("factory");
    const showClient = !isEmployee && enabled.has("client");
    // Manager doesn't have personal daily records — daily is for employees.
    const showDaily = enabled.has("daily") && company?.ledger_mode !== "basic" && !isManager;
    const showVouchers = !isEmployee && enabled.has("vouchers");
    const showDiscount = !isEmployee && !isManager && company?.discount_enabled === true;
    return (
        <AppShell>
            <div className="mb-6 sm:mb-8">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.pickerRecordsKicker}</p>
                <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{t.pickerRecordsTitle}</h1>
                <p className="mt-1 text-xs text-neutral-500 sm:text-sm">{t.pickerRecordsSubtitle}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                {showClient ? (
                    <Tile
                        to="/history/client"
                        icon={Users}
                        title={t.clientRecordsTitle}
                        subtitle={t.clientRecordsSubtitle}
                        color="bg-gradient-to-br from-white to-amber-50/40"
                        testId="picker-client-records"
                    />
                ) : null}
                {showFactory ? (
                    <Tile
                        to="/history/factory"
                        icon={Factory}
                        title={t.factoryRecordsTitle}
                        subtitle={t.factoryRecordsSubtitle}
                        color="bg-gradient-to-br from-white to-emerald-50/30"
                        testId="picker-factory-records"
                    />
                ) : null}
                {showDaily ? (
                    <Tile
                        to="/history/daily"
                        icon={CalendarClock}
                        title={t.dailyRecordsTitle}
                        subtitle={t.dailyRecordsSubtitle}
                        color="bg-gradient-to-br from-white to-sky-50/40"
                        testId="picker-daily-records"
                    />
                ) : null}
                {showVouchers ? (
                    <>
                        <Tile
                            to="/history/voucher_customer"
                            icon={Receipt}
                            title={t.voucherCustomerTile}
                            subtitle={t.voucherCustomerTileDesc}
                            color="bg-gradient-to-br from-white to-rose-50/40"
                            testId="picker-voucher-customer-records"
                        />
                        <Tile
                            to="/history/voucher_shop"
                            icon={Wallet}
                            title={t.voucherShopTile}
                            subtitle={t.voucherShopTileDesc}
                            color="bg-gradient-to-br from-white to-violet-50/40"
                            testId="picker-voucher-shop-records"
                        />
                    </>
                ) : null}
                {showDiscount ? (
                    <Tile
                        to="/discount-customers"
                        icon={Gift}
                        title={lang === "ar" ? "سجل خصم الزباين" : "Customer Discounts"}
                        subtitle={lang === "ar" ? "بطاقات ولاء — كل نقطة 5 ر.ع · حد أقصى 4 نقاط" : "Loyalty cards — 5 OMR per point · max 4"}
                        color="bg-gradient-to-br from-white to-amber-50/60"
                        testId="picker-discount-customers"
                    />
                ) : null}
            </div>
        </AppShell>
    );
}
